/**
 * Elite Batch 22 Part 2 — WebEngage Marketing Workflows (14-25)
 * Subscription Renewal Reminder, Payment Failure Recovery, Recurring Nudges for Active Users,
 * Inactive User Re-Engagement, Churn Risk Save Play, Wishlist Reminder,
 * Loyalty Points Expiry Reminder, VIP Tier Upgrade Celebration, Birthday Offer Journey,
 * Anniversary Milestone Journey, Geo-Targeted Store Visit Nudge, New Arrivals by Category.
 *
 * DESIGN RULES (inherited from prebuiltWorkflows.ts):
 * 1. Every variable MUST come from a real API response field or AI-generated output
 * 2. No magic variables — if a condition checks something, an upstream node must produce it
 * 3. Webhook triggers accept raw data — the FIRST node after a webhook trigger MUST be
 *    an AI extraction node that reads {webhook_payload} and outputs named variables.
 *    NEVER assume webhook payloads have specific field names (no {email}, {name}, etc.)
 * 4. Every condition has a REAL value (never blank, never "?")
 * 5. Services are pre-selected — user never sees "pick a service"
 * 6. Output variable names use the actual API response field paths
 * 7. Trigger inputFields should be EMPTY for webhooks — the AI extraction node declares outputs
 * 8. AI prompts for extraction should say "find the X regardless of format" to handle variations
 */

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}
function smsNode(
  id: string,
  label: string,
  y: number,
  to: string,
  message: string,
  x = 80,
): any {
  return n(id, "sendSMS", label, y, { sendSmsDetails: { to, message } }, x);
}
function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}

// =============================================================================
// ELITE BATCH 22 PART 2 — WEBENGAGE MARKETING WORKFLOWS (14-25)
// =============================================================================

export const ELITE_BATCH22_PART2: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ===================================================================
  // 14. SUBSCRIPTION RENEWAL REMINDER — Pattern F (8 nodes)
  // ===================================================================

  "Subscription Renewal Reminder": {
    summary:
      "Fires when a subscription renewal date approaches via WebEngage webhook, AI extracts subscription and billing details from the payload, checks Stripe for current payment method validity and upcoming invoice, branches on whether the payment method is expiring before renewal, sends a friendly renewal reminder email with plan benefits and renewal date, waits 3 days for action, checks if the subscription has been renewed or payment updated, and sends a value-recap follow-up or escalates with an urgent final reminder.",
    nodes: [
      trigger("WebEngage: Subscription renewal approaching", "webhook_app", {
        source: "webengage",
        event: "subscription.renewal_approaching",
        howItStarts:
          "Fires when WebEngage detects a subscription renewal date is within 14 days, sending the raw event payload with subscriber and plan details",
        setup:
          "Configure WebEngage webhook for renewal_approaching events. Set Stripe API key for billing lookups. Set OpenAI API key for payload extraction. Configure email sending credentials.",
        inputFields: [],
      }),

      // 2. AI extract subscription details
      aiNode(
        "2",
        "AI: Extract subscription and renewal details",
        280,
        "You are a subscription data extraction agent. From the incoming WebEngage renewal event payload, extract the subscriber email, full name, plan name, monthly or annual billing cycle, renewal date, subscription ID, current MRR amount, and how many months they have been subscribed. Return JSON: {subscriber_email, subscriber_name, plan_name, billing_cycle, renewal_date, subscription_id, mrr_amount, months_subscribed}",
        "WebEngage renewal approaching event payload: {webhook_payload}. Extract all subscription and renewal fields regardless of payload format or field naming conventions.",
        [
          { field: "subscriber_email", as: "subscriber_email", type: "string" },
          { field: "subscriber_name", as: "subscriber_name", type: "string" },
          { field: "plan_name", as: "plan_name", type: "string" },
          { field: "billing_cycle", as: "billing_cycle", type: "string" },
          { field: "renewal_date", as: "renewal_date", type: "string" },
          { field: "subscription_id", as: "subscription_id", type: "string" },
          { field: "mrr_amount", as: "mrr_amount", type: "string" },
          {
            field: "months_subscribed",
            as: "months_subscribed",
            type: "number",
          },
        ],
      ),

      // 3. Stripe check payment method and upcoming invoice
      n(
        "3",
        "hitApi",
        "Stripe: Check payment method and upcoming invoice",
        520,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.stripe.com/v1/subscriptions/{subscription_id}",
            headers: [
              { key: "Authorization", value: "Bearer {stripe_secret_key}" },
              {
                key: "Content-Type",
                value: "application/x-www-form-urlencoded",
              },
            ],
            body: [],
            variables: [
              {
                name: "default_payment_method.card.exp_month",
                customName: "card_exp_month",
                type: "number",
              },
              {
                name: "default_payment_method.card.exp_year",
                customName: "card_exp_year",
                type: "number",
              },
              {
                name: "default_payment_method.card.last4",
                customName: "card_last4",
                type: "string",
              },
              {
                name: "latest_invoice.amount_due",
                customName: "invoice_amount_due",
                type: "number",
              },
              {
                name: "status",
                customName: "subscription_status",
                type: "string",
              },
              {
                name: "current_period_end",
                customName: "period_end_timestamp",
                type: "number",
              },
            ],
          },
        },
      ),

      // 4. Condition: payment method expiring before renewal?
      n("4", "condition", "Payment method expiring before renewal?", 760, {
        conditionDetails: {
          conditions: [
            {
              variable: "subscription_status",
              operator: "equals",
              value: "active",
            },
          ],
          logicalOperator: "AND",
        },
      }),

      // 5. Email renewal reminder with plan benefits (True: active subscription)
      emailNode(
        "5",
        "Email: Renewal reminder with plan benefits recap",
        1000,
        "{subscriber_email}",
        "Your {plan_name} Renewal is Coming Up on {renewal_date}",
        "Hi {subscriber_name},\n\nYour {plan_name} ({billing_cycle}) subscription renews on {renewal_date}.\n\nYou have been a subscriber for {months_subscribed} months and your next invoice will be {mrr_amount}. Your card ending in {card_last4} will be charged automatically.\n\nHere is what you have been enjoying:\n- Full access to all {plan_name} features\n- Priority support and dedicated assistance\n- Exclusive member-only content and updates\n\nIf you need to update your payment method or have questions about your subscription, visit your account settings.\n\nThank you for being a valued subscriber!\n\nBest regards,\nThe Team",
      ),

      // 6. Delay 3 days
      n("6", "delayAction", "Wait 3 days for renewal action", 1240, {
        delayDetails: { duration: 3, unit: "days" },
      }),

      // 7. Condition: subscription renewed or payment updated?
      n("7", "condition", "Subscription renewed or payment updated?", 1480, {
        conditionDetails: {
          conditions: [
            {
              variable: "subscription_status",
              operator: "equals",
              value: "active",
            },
          ],
          logicalOperator: "AND",
        },
      }),

      // 8. Email urgent final reminder with value recap (False: still not renewed)
      emailNode(
        "8",
        "Email: Urgent final renewal reminder with value recap",
        1720,
        "{subscriber_email}",
        "Action Required: Your {plan_name} Subscription Expires Soon",
        "Hi {subscriber_name},\n\nThis is a friendly reminder that your {plan_name} subscription renews on {renewal_date} and we want to make sure there are no interruptions.\n\nOver the past {months_subscribed} months, you have relied on:\n- All premium features in {plan_name}\n- Continuous access without downtime\n- Priority support whenever you needed it\n\nPlease verify your payment method (card ending in {card_last4}) is up to date to avoid any service interruption.\n\nUpdate your payment method now or contact us if you have any questions.\n\nWe would hate to see you lose access!\n\nBest regards,\nThe Team",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_2"),
      e("4", "8", "output_2"),
    ],
  },

  // ===================================================================
  // 15. PAYMENT FAILURE RECOVERY — Pattern F (8 nodes)
  // ===================================================================

  "Payment Failure Recovery": {
    summary:
      "Fires when WebEngage detects a payment failure event, AI extracts the subscriber email, failed amount, failure reason, and subscription details from the payload, queries Stripe to check the current invoice status and retry schedule, branches on whether the failure is a soft decline (retry possible) vs hard decline, sends an empathetic payment update email with a direct link to update billing, waits 3 days for the customer to fix the issue, checks if the payment has been resolved, and sends a final escalation email warning of service suspension if still unresolved.",
    nodes: [
      trigger("WebEngage: Payment failure detected", "webhook_app", {
        source: "webengage",
        event: "payment.failed",
        howItStarts:
          "Fires when WebEngage receives a payment failure event from the billing system, sending the raw event payload with subscriber, invoice, and failure details",
        setup:
          "Configure WebEngage webhook for payment.failed events. Set Stripe API key for invoice lookups. Set OpenAI API key for payload extraction. Configure email sending credentials.",
        inputFields: [],
      }),

      // 2. AI extract payment failure details
      aiNode(
        "2",
        "AI: Extract payment failure and subscriber details",
        280,
        "You are a billing data extraction agent. From the incoming WebEngage payment failure event payload, extract the subscriber email, full name, failed payment amount, failure reason code, failure message, subscription ID, invoice ID, payment method type, and the number of retry attempts already made. Return JSON: {subscriber_email, subscriber_name, failed_amount, failure_reason_code, failure_message, subscription_id, invoice_id, payment_method_type, retry_attempts}",
        "WebEngage payment failure event payload: {webhook_payload}. Extract all payment failure fields regardless of payload format or field naming conventions.",
        [
          { field: "subscriber_email", as: "subscriber_email", type: "string" },
          { field: "subscriber_name", as: "subscriber_name", type: "string" },
          { field: "failed_amount", as: "failed_amount", type: "string" },
          {
            field: "failure_reason_code",
            as: "failure_reason_code",
            type: "string",
          },
          { field: "failure_message", as: "failure_message", type: "string" },
          { field: "subscription_id", as: "subscription_id", type: "string" },
          { field: "invoice_id", as: "invoice_id", type: "string" },
          {
            field: "payment_method_type",
            as: "payment_method_type",
            type: "string",
          },
          { field: "retry_attempts", as: "retry_attempts", type: "number" },
        ],
      ),

      // 3. Stripe check invoice status and retry schedule
      n("3", "hitApi", "Stripe: Check invoice status and retry schedule", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.stripe.com/v1/invoices/{invoice_id}",
          headers: [
            { key: "Authorization", value: "Bearer {stripe_secret_key}" },
            { key: "Content-Type", value: "application/x-www-form-urlencoded" },
          ],
          body: [],
          variables: [
            { name: "status", customName: "invoice_status", type: "string" },
            {
              name: "next_payment_attempt",
              customName: "next_retry_timestamp",
              type: "number",
            },
            {
              name: "attempt_count",
              customName: "total_attempts",
              type: "number",
            },
            {
              name: "amount_due",
              customName: "amount_due_cents",
              type: "number",
            },
            {
              name: "hosted_invoice_url",
              customName: "invoice_payment_url",
              type: "string",
            },
            {
              name: "subscription",
              customName: "stripe_subscription_id",
              type: "string",
            },
          ],
        },
      }),

      // 4. Condition: soft decline (retryable) vs hard decline?
      n("4", "condition", "Soft decline with automatic retry scheduled?", 760, {
        conditionDetails: {
          conditions: [
            { variable: "invoice_status", operator: "equals", value: "open" },
          ],
          logicalOperator: "AND",
        },
      }),

      // 5. Email payment update request with direct billing link (True: soft decline)
      emailNode(
        "5",
        "Email: Payment update request with billing link",
        1000,
        "{subscriber_email}",
        "Action Needed: Your Payment of {failed_amount} Did Not Go Through",
        "Hi {subscriber_name},\n\nWe attempted to process your payment of {failed_amount} but it was not successful. The reason provided was: {failure_message}.\n\nThis can happen for a number of reasons including an expired card, insufficient funds, or a temporary bank hold. The good news is this is usually easy to fix.\n\nPlease update your payment method using this secure link:\n{invoice_payment_url}\n\nWe will automatically retry the charge once your details are updated. Your subscription remains active during this time and we want to make sure you do not experience any interruption.\n\nIf you believe this is an error, please reply to this email and our billing team will help.\n\nBest regards,\nBilling Support",
      ),

      // 6. Delay 3 days
      n("6", "delayAction", "Wait 3 days for payment resolution", 1240, {
        delayDetails: { duration: 3, unit: "days" },
      }),

      // 7. Condition: payment resolved?
      n("7", "condition", "Payment resolved after waiting period?", 1480, {
        conditionDetails: {
          conditions: [
            { variable: "invoice_status", operator: "equals", value: "paid" },
          ],
          logicalOperator: "AND",
        },
      }),

      // 8. Email final escalation warning of service suspension (False: still unresolved)
      emailNode(
        "8",
        "Email: Final warning before service suspension",
        1720,
        "{subscriber_email}",
        "Urgent: Your Account Access is at Risk — Payment of {failed_amount} Still Outstanding",
        "Hi {subscriber_name},\n\nWe have been unable to process your payment of {failed_amount} after {total_attempts} attempts. Your payment method ({payment_method_type}) returned: {failure_message}.\n\nTo avoid losing access to your account and all your data, please update your payment information immediately:\n{invoice_payment_url}\n\nIf your payment is not resolved within the next 48 hours, your subscription will be paused and you will lose access to premium features.\n\nWe value you as a customer and want to help resolve this. If you are experiencing any billing difficulties, please reach out to our support team directly.\n\nBest regards,\nBilling Support",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_2"),
      e("4", "8", "output_2"),
    ],
  },

  // ===================================================================
  // 16. RECURRING NUDGES FOR ACTIVE USERS — Pattern A (8 nodes)
  // ===================================================================

  "Recurring Nudges for Active Users": {
    summary:
      "Fires when WebEngage detects an active user engagement event via webhook, AI extracts user activity details and feature usage patterns from the payload, queries WebEngage user profile API for engagement history and feature adoption, branches on whether the user has tried the target feature yet, sends a personalized feature education email highlighting unused capabilities, waits 2 days, checks if the user engaged with the recommended feature, and sends either a deeper engagement email with advanced tips or a different feature suggestion.",
    nodes: [
      trigger("WebEngage: Active user engagement detected", "webhook_app", {
        source: "webengage",
        event: "user.engagement_milestone",
        howItStarts:
          "Fires when WebEngage detects a user has reached an activity threshold (e.g., 5+ sessions this week) but has not explored key features, sending the raw event payload with user activity data",
        setup:
          "Configure WebEngage webhook for engagement milestone events. Set WebEngage REST API key for user profile lookups. Set OpenAI API key for payload extraction. Configure email sending credentials.",
        inputFields: [],
      }),

      // 2. AI extract user activity details
      aiNode(
        "2",
        "AI: Extract user activity and feature usage data",
        280,
        "You are a product analytics extraction agent. From the incoming WebEngage engagement event payload, extract the user email, full name, session count this week, most used features as a comma-separated list, last active timestamp, device type, app version, and the recommended feature they have not tried. Return JSON: {user_email, user_name, weekly_sessions, most_used_features, last_active, device_type, app_version, recommended_feature}",
        "WebEngage active user engagement event payload: {webhook_payload}. Extract all user activity and feature usage fields regardless of payload format or naming conventions.",
        [
          { field: "user_email", as: "user_email", type: "string" },
          { field: "user_name", as: "user_name", type: "string" },
          { field: "weekly_sessions", as: "weekly_sessions", type: "number" },
          {
            field: "most_used_features",
            as: "most_used_features",
            type: "string",
          },
          { field: "last_active", as: "last_active", type: "string" },
          { field: "device_type", as: "device_type", type: "string" },
          { field: "app_version", as: "app_version", type: "string" },
          {
            field: "recommended_feature",
            as: "recommended_feature",
            type: "string",
          },
        ],
      ),

      // 3. WebEngage lookup user engagement history
      n(
        "3",
        "hitApi",
        "WebEngage: Fetch user engagement history and feature adoption",
        520,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.webengage.com/v2/accounts/{webengage_account_id}/users/{user_email}",
            headers: [
              { key: "Authorization", value: "Bearer {webengage_api_key}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [],
            variables: [
              {
                name: "users.totalSessions",
                customName: "total_sessions",
                type: "number",
              },
              {
                name: "users.customAttributes.features_used_count",
                customName: "features_used_count",
                type: "number",
              },
              {
                name: "users.customAttributes.onboarding_completed",
                customName: "onboarding_completed",
                type: "boolean",
              },
              {
                name: "users.customAttributes.last_feature_discovered",
                customName: "last_feature_discovered",
                type: "string",
              },
              {
                name: "users.customAttributes.user_segment",
                customName: "user_segment",
                type: "string",
              },
            ],
          },
        },
      ),

      // 4. Condition: has user tried the recommended feature?
      n("4", "condition", "User has not tried recommended feature yet?", 760, {
        conditionDetails: {
          conditions: [
            {
              variable: "features_used_count",
              operator: "less_than",
              value: "5",
            },
          ],
          logicalOperator: "AND",
        },
      }),

      // 5. Email feature education for untried feature (True: has not tried)
      emailNode(
        "5",
        "Email: Feature education nudge for untried capability",
        1000,
        "{user_email}",
        "You are Crushing It — Now Try {recommended_feature}!",
        "Hi {user_name},\n\nWith {weekly_sessions} sessions this week, you are one of our most active users! We noticed you frequently use {most_used_features} but have not tried {recommended_feature} yet.\n\nHere is why you will love it:\n- It works perfectly with the features you already use\n- Users who enable {recommended_feature} see 40% better results\n- It takes less than 2 minutes to set up\n\nHere is a quick guide to get started with {recommended_feature}: https://app.example.com/features/{recommended_feature}\n\nGive it a try and let us know what you think!\n\nBest regards,\nProduct Team",
      ),

      // 6. Delay 2 days
      n("6", "delayAction", "Wait 2 days for feature exploration", 1240, {
        delayDetails: { duration: 2, unit: "days" },
      }),

      // 7. Condition: did user engage with recommended feature?
      n("7", "condition", "User engaged with recommended feature?", 1480, {
        conditionDetails: {
          conditions: [
            {
              variable: "features_used_count",
              operator: "greater_than",
              value: "5",
            },
          ],
          logicalOperator: "AND",
        },
      }),

      // 8. Email deeper engagement or alternative suggestion (False: still not engaged)
      emailNode(
        "8",
        "Email: Alternative feature suggestion or advanced tips",
        1720,
        "{user_email}",
        "Here is Another Way to Level Up Your Workflow",
        "Hi {user_name},\n\nWe know you are busy making the most of {most_used_features}. Since {recommended_feature} might not be the right fit right now, here are some advanced tips for what you already love:\n\n1. Power User Tip: Combine {most_used_features} with keyboard shortcuts for 2x speed\n2. Hidden Gem: Check out the advanced settings in your most-used features\n3. Community Pick: See how other {user_segment} users are getting creative\n\nOr if you prefer, explore our feature library to find something that matches your workflow: https://app.example.com/features\n\nYou have completed {total_sessions} total sessions — here is to many more!\n\nBest regards,\nProduct Team",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_2"),
      e("4", "8", "output_2"),
    ],
  },

  // ===================================================================
  // 17. INACTIVE USER RE-ENGAGEMENT — Pattern G (8 nodes)
  // ===================================================================

  "Inactive User Re-Engagement": {
    summary:
      "Runs on a daily schedule to identify users who have been inactive for 30+ days, queries WebEngage for users in the inactive segment with their last activity and purchase history, AI analyzes likely inactivity reasons and crafts a personalized re-engagement angle, branches on whether the user was a high-value customer based on lifetime spend, sends a personalized winback email highlighting what they are missing and new features since they left, waits 7 days for re-engagement, checks if the user has returned, and sends either a stronger offer or a sunset/preference-reset email for persistently inactive users.",
    nodes: [
      trigger("Schedule: Daily inactive user re-engagement scan", "schedule", {
        schedule: "Every day at 10:00 AM",
        howItStarts:
          "Runs daily to process users who crossed the 30-day inactivity threshold, triggering a re-engagement sequence for each inactive user",
        setup:
          "Configure daily schedule in WebEngage. Set WebEngage REST API key for user segment queries. Set OpenAI API key for personalization. Configure email sending credentials.",
        inputFields: [],
      }),

      // 2. WebEngage lookup inactive users with activity history
      n(
        "2",
        "hitApi",
        "WebEngage: Fetch inactive user segment with activity history",
        280,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.webengage.com/v2/accounts/{webengage_account_id}/users/search",
            headers: [
              { key: "Authorization", value: "Bearer {webengage_api_key}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              { key: "segment", value: "inactive_30_days" },
              {
                key: "attributes",
                value:
                  '["email","firstName","lastName","lastActiveDate","totalPurchases","lifetimeValue","favoriteCategory","lastPurchaseDate"]',
              },
            ],
            variables: [
              {
                name: "users[0].email",
                customName: "user_email",
                type: "string",
              },
              {
                name: "users[0].firstName",
                customName: "first_name",
                type: "string",
              },
              {
                name: "users[0].lastName",
                customName: "last_name",
                type: "string",
              },
              {
                name: "users[0].lastActiveDate",
                customName: "last_active_date",
                type: "string",
              },
              {
                name: "users[0].totalPurchases",
                customName: "total_purchases",
                type: "number",
              },
              {
                name: "users[0].lifetimeValue",
                customName: "lifetime_value",
                type: "number",
              },
              {
                name: "users[0].favoriteCategory",
                customName: "favorite_category",
                type: "string",
              },
              {
                name: "users[0].lastPurchaseDate",
                customName: "last_purchase_date",
                type: "string",
              },
            ],
          },
        },
      ),

      // 3. AI analyze inactivity reason and personalize approach
      aiNode(
        "3",
        "AI: Analyze inactivity reason and craft re-engagement angle",
        520,
        "You are a customer retention strategist. Analyze the inactive user profile and determine the most likely reason for disengagement: lost interest, found competitor, life event, seasonal buyer, or dissatisfied. Then craft a personalized re-engagement message angle. Return JSON: {inactivity_reason, confidence_score, reengagement_angle, personalized_hook, days_inactive, value_tier}",
        "Inactive user profile — Email: {user_email}, Name: {first_name} {last_name}, Last active: {last_active_date}, Total purchases: {total_purchases}, Lifetime value: {lifetime_value}, Favorite category: {favorite_category}, Last purchase: {last_purchase_date}. Determine why they left and the best approach to bring them back.",
        [
          {
            field: "inactivity_reason",
            as: "inactivity_reason",
            type: "string",
          },
          { field: "confidence_score", as: "confidence_score", type: "number" },
          {
            field: "reengagement_angle",
            as: "reengagement_angle",
            type: "string",
          },
          {
            field: "personalized_hook",
            as: "personalized_hook",
            type: "string",
          },
          { field: "days_inactive", as: "days_inactive", type: "number" },
          { field: "value_tier", as: "value_tier", type: "string" },
        ],
      ),

      // 4. Condition: high-value customer?
      n("4", "condition", "High-value customer based on lifetime spend?", 760, {
        conditionDetails: {
          conditions: [
            {
              variable: "lifetime_value",
              operator: "greater_than",
              value: "200",
            },
          ],
          logicalOperator: "AND",
        },
      }),

      // 5. Email personalized winback with what they are missing (True/False both get email, but HV gets richer content)
      emailNode(
        "5",
        "Email: Personalized winback highlighting new features",
        1000,
        "{user_email}",
        "We Miss You, {first_name} — Here is What is New",
        "Hi {first_name},\n\nIt has been {days_inactive} days since your last visit and a lot has changed! As one of our valued customers with {total_purchases} purchases, we wanted to personally reach out.\n\n{personalized_hook}\n\nSince you have been away, here is what is new:\n- Fresh arrivals in {favorite_category} handpicked for you\n- Improved user experience with faster checkout\n- New loyalty rewards you have been earning even while away\n\nAs a thank you for being a loyal customer, enjoy 20% off your next order with code WELCOMEBACK20.\n\nWe would love to see you again!\n\nBest regards,\nThe Team",
      ),

      // 6. Delay 7 days
      n("6", "delayAction", "Wait 7 days for re-engagement response", 1240, {
        delayDetails: { duration: 7, unit: "days" },
      }),

      // 7. Condition: user returned and active again?
      n("7", "condition", "User returned and active again?", 1480, {
        conditionDetails: {
          conditions: [
            { variable: "days_inactive", operator: "less_than", value: "7" },
          ],
          logicalOperator: "AND",
        },
      }),

      // 8. Email sunset or preference reset for persistently inactive (False: still inactive)
      emailNode(
        "8",
        "Email: Final offer or preference reset for inactive user",
        1720,
        "{user_email}",
        "Last Chance: Stay Connected or Update Your Preferences",
        "Hi {first_name},\n\nWe have tried reaching out but have not heard back from you. We respect your inbox and want to make sure we are only sending you things you care about.\n\nYou have two options:\n\n1. Come back with a special offer: Use code LASTCHANCE25 for 25% off — our biggest discount, just for you.\n\n2. Update your preferences: Tell us what you want to hear about and how often at: https://app.example.com/preferences\n\nIf we do not hear from you in the next 14 days, we will reduce our emails to keep your inbox clean. You can always come back anytime.\n\nWe genuinely appreciate the {total_purchases} orders you have placed with us over your {favorite_category} journey.\n\nWarmly,\nThe Team",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_2"),
      e("4", "5", "output_2"),
    ],
  },

  // ===================================================================
  // 18. CHURN RISK SAVE PLAY — Pattern L (8 nodes)
  // ===================================================================

  "Churn Risk Save Play": {
    summary:
      "Fires when WebEngage detects a usage drop signal indicating churn risk via webhook, AI extracts account context and usage decline metrics from the payload, queries WebEngage CRM for full account health including support tickets and feature adoption, AI analyzes the signal to recommend a save action (education, check-in, offer, or escalation), branches on whether this is an expansion opportunity or a save-risk situation, sends a contextual save email with personalized intervention, waits 5 days for stabilization, and checks usage trend to send either a follow-up save play or an internal Slack escalation to the CS team.",
    nodes: [
      trigger("WebEngage: Usage drop churn risk detected", "webhook_app", {
        source: "webengage",
        event: "user.usage_drop_detected",
        howItStarts:
          "Fires when WebEngage churn prediction model flags a user with significant usage decline, sending the raw event payload with usage metrics and risk indicators",
        setup:
          "Configure WebEngage webhook for usage_drop_detected events. Set WebEngage REST API key for account lookups. Set OpenAI API key for analysis. Configure email and Slack webhook credentials.",
        inputFields: [],
      }),

      // 2. WebEngage CRM lookup full account health
      n(
        "2",
        "hitApi",
        "WebEngage: Fetch account health and support history",
        280,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.webengage.com/v2/accounts/{webengage_account_id}/users/{webhook_payload}",
            headers: [
              { key: "Authorization", value: "Bearer {webengage_api_key}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [],
            variables: [
              { name: "users.email", customName: "user_email", type: "string" },
              {
                name: "users.firstName",
                customName: "user_name",
                type: "string",
              },
              {
                name: "users.customAttributes.account_health_score",
                customName: "health_score",
                type: "number",
              },
              {
                name: "users.customAttributes.open_support_tickets",
                customName: "open_tickets",
                type: "number",
              },
              {
                name: "users.customAttributes.features_adopted",
                customName: "features_adopted",
                type: "number",
              },
              {
                name: "users.customAttributes.plan_name",
                customName: "plan_name",
                type: "string",
              },
              {
                name: "users.customAttributes.monthly_spend",
                customName: "monthly_spend",
                type: "string",
              },
              {
                name: "users.customAttributes.sessions_last_30d",
                customName: "sessions_last_30d",
                type: "number",
              },
            ],
          },
        },
      ),

      // 3. AI analyze churn signal and recommend save action
      aiNode(
        "3",
        "AI: Analyze churn signal and recommend save action",
        520,
        "You are a customer success strategist. Analyze the account health data to determine the churn risk severity (low, medium, high, critical) and recommend the best save action. Consider the health score, open support tickets, feature adoption rate, and recent usage decline. Return JSON: {risk_severity, primary_churn_driver, recommended_action, action_type, personalized_message, escalation_needed}",
        "Account health data — Email: {user_email}, Name: {user_name}, Health score: {health_score}, Open tickets: {open_tickets}, Features adopted: {features_adopted}, Plan: {plan_name}, Monthly spend: {monthly_spend}, Sessions last 30d: {sessions_last_30d}. Determine the churn risk severity and best intervention strategy.",
        [
          { field: "risk_severity", as: "risk_severity", type: "string" },
          {
            field: "primary_churn_driver",
            as: "primary_churn_driver",
            type: "string",
          },
          {
            field: "recommended_action",
            as: "recommended_action",
            type: "string",
          },
          { field: "action_type", as: "action_type", type: "string" },
          {
            field: "personalized_message",
            as: "personalized_message",
            type: "string",
          },
          {
            field: "escalation_needed",
            as: "escalation_needed",
            type: "boolean",
          },
        ],
      ),

      // 4. Condition: expansion opportunity vs save-risk?
      n("4", "condition", "Save-risk requiring intervention?", 760, {
        conditionDetails: {
          conditions: [
            { variable: "risk_severity", operator: "not_equals", value: "low" },
          ],
          logicalOperator: "AND",
        },
      }),

      // 5. Email contextual save intervention (True: needs save)
      emailNode(
        "5",
        "Email: Personalized churn save intervention",
        1000,
        "{user_email}",
        "We Want to Help — Let Us Make {plan_name} Work Better for You",
        "Hi {user_name},\n\nWe noticed your usage has changed recently and wanted to check in personally.\n\n{personalized_message}\n\nBased on your {plan_name} plan, here is what we recommend:\n- {recommended_action}\n- A complimentary strategy session with our success team\n- Priority support for any open issues (you have {open_tickets} tickets we are actively working on)\n\nYou have adopted {features_adopted} features so far, and we think there are a few more that could significantly improve your experience.\n\nWould you like to schedule a 15-minute call? Simply reply to this email or book a time at: https://app.example.com/book-success-call\n\nWe are here to help!\n\nBest regards,\nCustomer Success Team",
      ),

      // 6. Delay 5 days
      n("6", "delayAction", "Wait 5 days for usage stabilization", 1240, {
        delayDetails: { duration: 5, unit: "days" },
      }),

      // 7. Condition: usage stabilized or converted?
      n("7", "condition", "Usage stabilized after intervention?", 1480, {
        conditionDetails: {
          conditions: [
            { variable: "health_score", operator: "greater_than", value: "50" },
          ],
          logicalOperator: "AND",
        },
      }),

      // 8. Slack escalation to CS team (False: still declining)
      slackNode(
        "8",
        "Slack: Escalate to CS team for critical churn risk",
        1720,
        ":rotating_light: *Critical Churn Risk — Immediate Action Required*\n- Account: {user_name} ({user_email})\n- Plan: {plan_name} ({monthly_spend}/mo)\n- Risk Severity: {risk_severity}\n- Primary Driver: {primary_churn_driver}\n- Health Score: {health_score}\n- Sessions (30d): {sessions_last_30d}\n- Open Tickets: {open_tickets}\n- Features Adopted: {features_adopted}\n- Recommended: {recommended_action}\n- Initial email sent 5 days ago — no usage improvement detected. Please reach out directly.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_2"),
      e("4", "8", "output_2"),
    ],
  },

  // ===================================================================
  // 19. WISHLIST REMINDER — Pattern D (8 nodes)
  // ===================================================================

  "Wishlist Reminder": {
    summary:
      "Fires when WebEngage detects a wishlist item event (price drop, back in stock, or low inventory) via webhook, AI extracts the product details, wishlist context, and event type from the payload, queries WebEngage for the user profile to verify interest and consent preferences, branches on urgency level based on how large the price drop is or how low the stock is, sends an immediate wishlist alert email with the product image and a direct purchase link, waits 24 hours, checks if the user has purchased the item, and sends either a similar-items follow-up or an urgency reminder if the item is still available.",
    nodes: [
      trigger("WebEngage: Wishlist item event detected", "webhook_app", {
        source: "webengage",
        event: "wishlist.item_event",
        howItStarts:
          "Fires when WebEngage detects a price drop, restock, or low inventory event for a product on a user's wishlist, sending the raw event payload with product and user details",
        setup:
          "Configure WebEngage webhook for wishlist item events. Set WebEngage REST API key for user profile lookups. Set OpenAI API key for payload extraction. Configure email sending credentials.",
        inputFields: [],
      }),

      // 2. AI extract wishlist and product details
      aiNode(
        "2",
        "AI: Extract wishlist item and event details",
        280,
        "You are a product event extraction agent. From the incoming WebEngage wishlist event payload, extract the user email, full name, product name, product ID, original price, current price, discount percentage, stock quantity remaining, event type (price_drop, back_in_stock, or low_inventory), product category, product image URL, and the direct product page URL. Return JSON: {user_email, user_name, product_name, product_id, original_price, current_price, discount_percentage, stock_remaining, event_type, product_category, product_image_url, product_url}",
        "WebEngage wishlist item event payload: {webhook_payload}. Extract all product and wishlist event fields regardless of payload format or field naming conventions.",
        [
          { field: "user_email", as: "user_email", type: "string" },
          { field: "user_name", as: "user_name", type: "string" },
          { field: "product_name", as: "product_name", type: "string" },
          { field: "product_id", as: "product_id", type: "string" },
          { field: "original_price", as: "original_price", type: "string" },
          { field: "current_price", as: "current_price", type: "string" },
          {
            field: "discount_percentage",
            as: "discount_percentage",
            type: "string",
          },
          { field: "stock_remaining", as: "stock_remaining", type: "number" },
          { field: "event_type", as: "event_type", type: "string" },
          { field: "product_category", as: "product_category", type: "string" },
          {
            field: "product_image_url",
            as: "product_image_url",
            type: "string",
          },
          { field: "product_url", as: "product_url", type: "string" },
        ],
      ),

      // 3. WebEngage verify user interest and consent
      n(
        "3",
        "hitApi",
        "WebEngage: Verify user interest and consent preferences",
        520,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.webengage.com/v2/accounts/{webengage_account_id}/users/{user_email}",
            headers: [
              { key: "Authorization", value: "Bearer {webengage_api_key}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [],
            variables: [
              {
                name: "users.customAttributes.email_opt_in",
                customName: "email_opt_in",
                type: "boolean",
              },
              {
                name: "users.customAttributes.wishlist_count",
                customName: "wishlist_count",
                type: "number",
              },
              {
                name: "users.customAttributes.last_purchase_date",
                customName: "last_purchase_date",
                type: "string",
              },
              {
                name: "users.customAttributes.preferred_category",
                customName: "preferred_category",
                type: "string",
              },
              {
                name: "users.customAttributes.total_orders",
                customName: "total_orders",
                type: "number",
              },
            ],
          },
        },
      ),

      // 4. Condition: high urgency (large price drop or very low stock)?
      n(
        "4",
        "condition",
        "High urgency: large discount or very low stock?",
        760,
        {
          conditionDetails: {
            conditions: [
              {
                variable: "stock_remaining",
                operator: "less_than",
                value: "10",
              },
            ],
            logicalOperator: "AND",
          },
        },
      ),

      // 5. Email immediate wishlist alert with product and purchase link
      emailNode(
        "5",
        "Email: Wishlist item alert with product details",
        1000,
        "{user_email}",
        "Your Wishlisted Item — {product_name} — Has a {event_type} Update!",
        "Hi {user_name},\n\nGreat news about an item on your wishlist!\n\n{product_name}\nWas: {original_price} → Now: {current_price} ({discount_percentage} off)\nOnly {stock_remaining} left in stock!\n\nThis {product_category} item has been on your wishlist and we did not want you to miss this. With only {stock_remaining} remaining, it could sell out quickly.\n\nShop now: {product_url}\n\nYou have {wishlist_count} items on your wishlist — we are keeping an eye on all of them for you.\n\nHappy shopping!\n\nBest regards,\nThe Team",
      ),

      // 6. Delay 24 hours
      n("6", "delayAction", "Wait 24 hours for purchase decision", 1240, {
        delayDetails: { duration: 24, unit: "hours" },
      }),

      // 7. Condition: user purchased the item?
      n("7", "condition", "User purchased the wishlisted item?", 1480, {
        conditionDetails: {
          conditions: [
            { variable: "total_orders", operator: "greater_than", value: "0" },
          ],
          logicalOperator: "AND",
        },
      }),

      // 8. Email urgency follow-up or similar items
      emailNode(
        "8",
        "Email: Urgency reminder or similar items suggestion",
        1720,
        "{user_email}",
        "Still Thinking About {product_name}? It Will Not Last Long",
        "Hi {user_name},\n\nJust a quick reminder that {product_name} from your wishlist is still available at {current_price} (down from {original_price}).\n\nWith only {stock_remaining} left, this deal will not last much longer.\n\nIf {product_name} is not quite right, here are similar items in {product_category} you might love:\n- Browse more {product_category}: https://app.example.com/category/{product_category}\n- See trending items in your preferred category: {preferred_category}\n\nDo not let this one get away!\n\nShop now: {product_url}\n\nBest regards,\nThe Team",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_2"),
      e("4", "5", "output_2"),
    ],
  },

  // ===================================================================
  // 20. LOYALTY POINTS EXPIRY REMINDER — Pattern H (8 nodes)
  // ===================================================================

  "Loyalty Points Expiry Reminder": {
    summary:
      "Runs on a schedule to check for loyalty points nearing expiry, queries WebEngage for the user's loyalty profile including points balance, tier status, and LTV, branches on VIP tier to determine communication urgency and reward value, sends a points expiry reminder email with redemption suggestions tailored to their tier, waits 5 days for redemption, checks if the points have been redeemed, sends a final urgency reminder for unredeemed points, and logs the milestone and redemption status to Google Sheets for loyalty program tracking.",
    nodes: [
      trigger("Schedule: Check loyalty points approaching expiry", "schedule", {
        schedule: "Every day at 9:00 AM",
        howItStarts:
          "Runs daily to identify users whose loyalty points are expiring within 14 days, triggering a reminder sequence for each user with expiring points",
        setup:
          "Configure daily schedule. Set WebEngage REST API key for loyalty segment queries. Configure email sending credentials. Set Google Sheets API credentials for logging.",
        inputFields: [],
      }),

      // 2. WebEngage lookup loyalty data and LTV
      n(
        "2",
        "hitApi",
        "WebEngage: Fetch loyalty profile with points and tier data",
        280,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.webengage.com/v2/accounts/{webengage_account_id}/users/search",
            headers: [
              { key: "Authorization", value: "Bearer {webengage_api_key}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              { key: "segment", value: "points_expiring_14d" },
              {
                key: "attributes",
                value:
                  '["email","firstName","loyaltyTier","pointsBalance","pointsExpiring","pointsExpiryDate","lifetimeValue","totalRedemptions","lastRedemptionDate"]',
              },
            ],
            variables: [
              {
                name: "users[0].email",
                customName: "user_email",
                type: "string",
              },
              {
                name: "users[0].firstName",
                customName: "user_name",
                type: "string",
              },
              {
                name: "users[0].loyaltyTier",
                customName: "loyalty_tier",
                type: "string",
              },
              {
                name: "users[0].pointsBalance",
                customName: "points_balance",
                type: "number",
              },
              {
                name: "users[0].pointsExpiring",
                customName: "points_expiring",
                type: "number",
              },
              {
                name: "users[0].pointsExpiryDate",
                customName: "points_expiry_date",
                type: "string",
              },
              {
                name: "users[0].lifetimeValue",
                customName: "lifetime_value",
                type: "number",
              },
              {
                name: "users[0].totalRedemptions",
                customName: "total_redemptions",
                type: "number",
              },
              {
                name: "users[0].lastRedemptionDate",
                customName: "last_redemption_date",
                type: "string",
              },
            ],
          },
        },
      ),

      // 3. Condition: VIP tier?
      n("3", "condition", "User is VIP tier (Gold or above)?", 520, {
        conditionDetails: {
          conditions: [
            { variable: "loyalty_tier", operator: "equals", value: "Gold" },
          ],
          logicalOperator: "AND",
        },
      }),

      // 4. Email points expiry reminder with redemption suggestions
      emailNode(
        "4",
        "Email: Points expiry reminder with redemption suggestions",
        760,
        "{user_email}",
        "{user_name}, Your {points_expiring} Points Expire on {points_expiry_date}!",
        "Hi {user_name},\n\nDid you know you have {points_expiring} loyalty points expiring on {points_expiry_date}? That is real value you do not want to lose!\n\nYour current balance: {points_balance} points ({loyalty_tier} tier)\nExpiring soon: {points_expiring} points\n\nHere are some great ways to use them:\n- Redeem for a discount on your next purchase\n- Convert to a gift card for a friend\n- Unlock exclusive {loyalty_tier} tier rewards\n- Donate to a charity partner\n\nWith {total_redemptions} successful redemptions in the past, you know how easy it is!\n\nRedeem now: https://app.example.com/loyalty/redeem\n\nBest regards,\nLoyalty Team",
      ),

      // 5. Delay 5 days
      n("5", "delayAction", "Wait 5 days for points redemption", 1000, {
        delayDetails: { duration: 5, unit: "days" },
      }),

      // 6. Condition: points redeemed?
      n("6", "condition", "Points redeemed since reminder?", 1240, {
        conditionDetails: {
          conditions: [
            {
              variable: "total_redemptions",
              operator: "greater_than",
              value: "0",
            },
          ],
          logicalOperator: "AND",
        },
      }),

      // 7. Email final urgency redemption reminder
      emailNode(
        "7",
        "Email: Final urgency reminder before points expire",
        1480,
        "{user_email}",
        "Last Chance: {points_expiring} Points Expire in Days!",
        "Hi {user_name},\n\nThis is your final reminder — {points_expiring} loyalty points are about to expire on {points_expiry_date}.\n\nAs a {loyalty_tier} member, you have earned these points and we want to make sure you use them before they are gone.\n\nQuick redemption options:\n- Apply points to your cart at checkout (takes 10 seconds)\n- Browse our rewards catalog for exclusive items\n- Stack points with current promotions for maximum savings\n\nOnce they expire, these points cannot be recovered. Do not let them go to waste!\n\nRedeem before {points_expiry_date}: https://app.example.com/loyalty/redeem\n\nBest regards,\nLoyalty Team",
      ),

      // 8. Google Sheets log milestone and redemption status
      sheetsNode(
        "8",
        "Google Sheets: Log points expiry milestone and status",
        1720,
        "LoyaltyPointsExpiry",
        '[["{user_email}","{user_name}","{loyalty_tier}","{points_balance}","{points_expiring}","{points_expiry_date}","{total_redemptions}","{lifetime_value}","reminder_sent"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_2"),
      e("7", "8"),
      e("3", "4", "output_2"),
    ],
  },

  // ===================================================================
  // 21. VIP TIER UPGRADE CELEBRATION — Pattern H (8 nodes)
  // ===================================================================

  "VIP Tier Upgrade Celebration": {
    summary:
      "Fires when WebEngage detects a user has been upgraded to a new loyalty tier via webhook, queries WebEngage for the user's full loyalty profile including new tier perks, LTV, and purchase history, branches on whether the new tier is the top tier (Platinum/Diamond) for premium treatment, sends a celebration email with new tier benefits and an exclusive welcome reward, waits 5 days for reward redemption, checks if the user has redeemed the tier upgrade reward, sends a perks education email reminding them of unused benefits, and logs the tier upgrade event and redemption status to Google Sheets.",
    nodes: [
      trigger("WebEngage: Loyalty tier upgrade detected", "webhook_app", {
        source: "webengage",
        event: "loyalty.tier_upgraded",
        howItStarts:
          "Fires when WebEngage detects a user has qualified for a higher loyalty tier based on their spending or activity, sending the raw event payload with tier change details",
        setup:
          "Configure WebEngage webhook for loyalty tier upgrade events. Set WebEngage REST API key for user profile lookups. Set OpenAI API key for extraction. Configure email sending credentials and Google Sheets API.",
        inputFields: [],
      }),

      // 2. AI extract tier upgrade details
      aiNode(
        "2",
        "AI: Extract tier upgrade and loyalty details",
        280,
        "You are a loyalty program data extraction agent. From the incoming WebEngage tier upgrade event payload, extract the user email, full name, previous tier, new tier, points balance, points earned to reach new tier, total lifetime spend, total orders count, member since date, and the new tier welcome reward details. Return JSON: {user_email, user_name, previous_tier, new_tier, points_balance, points_earned_for_tier, lifetime_spend, total_orders, member_since, welcome_reward}",
        "WebEngage loyalty tier upgrade event payload: {webhook_payload}. Extract all tier upgrade and loyalty fields regardless of payload format or field naming conventions.",
        [
          { field: "user_email", as: "user_email", type: "string" },
          { field: "user_name", as: "user_name", type: "string" },
          { field: "previous_tier", as: "previous_tier", type: "string" },
          { field: "new_tier", as: "new_tier", type: "string" },
          { field: "points_balance", as: "points_balance", type: "number" },
          {
            field: "points_earned_for_tier",
            as: "points_earned_for_tier",
            type: "number",
          },
          { field: "lifetime_spend", as: "lifetime_spend", type: "string" },
          { field: "total_orders", as: "total_orders", type: "number" },
          { field: "member_since", as: "member_since", type: "string" },
          { field: "welcome_reward", as: "welcome_reward", type: "string" },
        ],
      ),

      // 3. Condition: top tier (Platinum/Diamond)?
      n("3", "condition", "Upgraded to top tier (Platinum or Diamond)?", 520, {
        conditionDetails: {
          conditions: [
            { variable: "new_tier", operator: "equals", value: "Platinum" },
          ],
          logicalOperator: "AND",
        },
      }),

      // 4. Email celebration with new tier benefits and welcome reward
      emailNode(
        "4",
        "Email: Tier upgrade celebration with exclusive reward",
        760,
        "{user_email}",
        "Congratulations {user_name}! Welcome to {new_tier} Tier!",
        "Hi {user_name},\n\nAmazing news — you have been upgraded from {previous_tier} to {new_tier} tier! This is a huge milestone and we are thrilled to celebrate with you.\n\nYour journey so far:\n- Member since: {member_since}\n- Total orders: {total_orders}\n- Lifetime spend: {lifetime_spend}\n- Points earned: {points_earned_for_tier}\n\nAs a {new_tier} member, you now unlock:\n- Exclusive {new_tier}-only promotions and early access\n- Higher points earning rate on every purchase\n- Free expedited shipping on all orders\n- Priority customer support\n- Birthday double-points bonus\n\nTo celebrate your upgrade, here is your exclusive welcome reward:\n{welcome_reward}\n\nClaim your reward: https://app.example.com/loyalty/claim-reward\n\nWelcome to the club!\n\nBest regards,\nLoyalty Team",
      ),

      // 5. Delay 5 days
      n("5", "delayAction", "Wait 5 days for reward redemption", 1000, {
        delayDetails: { duration: 5, unit: "days" },
      }),

      // 6. Condition: reward redeemed?
      n("6", "condition", "Tier upgrade reward redeemed?", 1240, {
        conditionDetails: {
          conditions: [
            {
              variable: "points_balance",
              operator: "greater_than",
              value: "0",
            },
          ],
          logicalOperator: "AND",
        },
      }),

      // 7. Email perks education or redemption reminder
      emailNode(
        "7",
        "Email: Perks education and redemption reminder",
        1480,
        "{user_email}",
        "{user_name}, Make the Most of Your {new_tier} Perks!",
        "Hi {user_name},\n\nIt has been a few days since your upgrade to {new_tier} tier and we want to make sure you are taking full advantage of your new perks!\n\nDid you claim your welcome reward yet?\n{welcome_reward}\nClaim here: https://app.example.com/loyalty/claim-reward\n\nHere are some {new_tier} benefits you might not know about:\n\n1. Stack your {points_balance} points with {new_tier}-exclusive sales for maximum savings\n2. Use your priority support line for instant help\n3. Access the {new_tier} members-only collection\n4. Earn bonus points on purchases every weekend\n\nWith {total_orders} orders under your belt, you deserve every perk!\n\nExplore your benefits: https://app.example.com/loyalty/perks\n\nBest regards,\nLoyalty Team",
      ),

      // 8. Google Sheets log tier upgrade and redemption status
      sheetsNode(
        "8",
        "Google Sheets: Log tier upgrade event and redemption",
        1720,
        "TierUpgradeLog",
        '[["{user_email}","{user_name}","{previous_tier}","{new_tier}","{points_balance}","{lifetime_spend}","{total_orders}","{member_since}","{welcome_reward}","logged"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_2"),
      e("7", "8"),
      e("3", "4", "output_2"),
    ],
  },

  // ===================================================================
  // 22. BIRTHDAY OFFER JOURNEY — Pattern H (8 nodes)
  // ===================================================================

  "Birthday Offer Journey": {
    summary:
      "Runs on a daily schedule to identify users whose birthday is within 3 days, queries WebEngage for the user's loyalty profile including tier, purchase history, and preferred categories, branches on VIP tier to offer a higher-value birthday reward, sends a personalized birthday email with a tier-appropriate discount code and product suggestions based on favorite categories, waits 5 days for offer redemption, checks if the birthday offer was redeemed, sends a redemption reminder or next-level teaser, and logs the birthday campaign and redemption to Google Sheets.",
    nodes: [
      trigger("Schedule: Daily birthday user identification", "schedule", {
        schedule: "Every day at 8:00 AM",
        howItStarts:
          "Runs daily to find users whose birthday falls within the next 3 days, triggering a personalized birthday offer for each qualifying user",
        setup:
          "Configure daily schedule. Set WebEngage REST API key for birthday segment queries. Configure email sending credentials. Set Google Sheets API credentials for campaign logging.",
        inputFields: [],
      }),

      // 2. WebEngage lookup loyalty data and LTV for birthday users
      n(
        "2",
        "hitApi",
        "WebEngage: Fetch birthday user loyalty profile and preferences",
        280,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.webengage.com/v2/accounts/{webengage_account_id}/users/search",
            headers: [
              { key: "Authorization", value: "Bearer {webengage_api_key}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              { key: "segment", value: "birthday_within_3_days" },
              {
                key: "attributes",
                value:
                  '["email","firstName","birthday","loyaltyTier","pointsBalance","lifetimeValue","totalOrders","favoriteCategory","lastPurchaseDate","averageOrderValue"]',
              },
            ],
            variables: [
              {
                name: "users[0].email",
                customName: "user_email",
                type: "string",
              },
              {
                name: "users[0].firstName",
                customName: "user_name",
                type: "string",
              },
              {
                name: "users[0].birthday",
                customName: "birthday_date",
                type: "string",
              },
              {
                name: "users[0].loyaltyTier",
                customName: "loyalty_tier",
                type: "string",
              },
              {
                name: "users[0].pointsBalance",
                customName: "points_balance",
                type: "number",
              },
              {
                name: "users[0].lifetimeValue",
                customName: "lifetime_value",
                type: "number",
              },
              {
                name: "users[0].totalOrders",
                customName: "total_orders",
                type: "number",
              },
              {
                name: "users[0].favoriteCategory",
                customName: "favorite_category",
                type: "string",
              },
              {
                name: "users[0].lastPurchaseDate",
                customName: "last_purchase_date",
                type: "string",
              },
              {
                name: "users[0].averageOrderValue",
                customName: "avg_order_value",
                type: "string",
              },
            ],
          },
        },
      ),

      // 3. Condition: VIP tier?
      n(
        "3",
        "condition",
        "User is VIP tier for premium birthday reward?",
        520,
        {
          conditionDetails: {
            conditions: [
              { variable: "loyalty_tier", operator: "equals", value: "Gold" },
            ],
            logicalOperator: "AND",
          },
        },
      ),

      // 4. Email personalized birthday offer (VIP gets bigger reward, all tiers get an offer)
      emailNode(
        "4",
        "Email: Personalized birthday offer with product suggestions",
        760,
        "{user_email}",
        "Happy Birthday, {user_name}! Here is a Special Gift Just for You",
        "Hi {user_name},\n\nHappy Birthday! We are celebrating YOU today and wanted to make your day extra special.\n\nAs a valued {loyalty_tier} member with {total_orders} orders, you deserve something amazing. Here is your exclusive birthday treat:\n\nUse code BDAY30 for 30% off your entire order — valid for 7 days!\n\nBased on your love of {favorite_category}, we picked some items you will adore:\n- New arrivals in {favorite_category}\n- Best sellers you have not tried yet\n- Exclusive birthday collection items\n\nShop your birthday picks: https://app.example.com/birthday-picks\n\nPlus, you have {points_balance} loyalty points ready to stack with your birthday discount for even bigger savings!\n\nHave a wonderful birthday!\n\nWith love,\nThe Team",
      ),

      // 5. Delay 5 days
      n("5", "delayAction", "Wait 5 days for birthday offer redemption", 1000, {
        delayDetails: { duration: 5, unit: "days" },
      }),

      // 6. Condition: birthday offer redeemed?
      n("6", "condition", "Birthday offer redeemed?", 1240, {
        conditionDetails: {
          conditions: [
            { variable: "total_orders", operator: "greater_than", value: "0" },
          ],
          logicalOperator: "AND",
        },
      }),

      // 7. Email redemption reminder or next-level teaser
      emailNode(
        "7",
        "Email: Birthday offer reminder with extended deadline",
        1480,
        "{user_email}",
        "{user_name}, Your Birthday Reward Expires Soon!",
        "Hi {user_name},\n\nWe hope you had a fantastic birthday! Just a friendly reminder that your birthday reward (code BDAY30 for 30% off) expires in 2 days.\n\nHere is a quick peek at what is trending in {favorite_category}:\n- Top-rated items other {loyalty_tier} members are loving\n- New drops that just arrived this week\n- Items under {avg_order_value} to treat yourself\n\nRemember, you can combine your {points_balance} loyalty points with BDAY30 for maximum savings.\n\nDo not let your birthday gift go to waste!\n\nShop now: https://app.example.com/birthday-picks\n\nBest regards,\nThe Team",
      ),

      // 8. Google Sheets log birthday campaign and redemption status
      sheetsNode(
        "8",
        "Google Sheets: Log birthday campaign and redemption",
        1720,
        "BirthdayCampaignLog",
        '[["{user_email}","{user_name}","{birthday_date}","{loyalty_tier}","{points_balance}","{lifetime_value}","{total_orders}","{favorite_category}","logged"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_2"),
      e("7", "8"),
      e("3", "4", "output_2"),
    ],
  },

  // ===================================================================
  // 23. ANNIVERSARY MILESTONE JOURNEY — Pattern H (8 nodes)
  // ===================================================================

  "Anniversary Milestone Journey": {
    summary:
      "Runs on a daily schedule to identify users reaching their customer anniversary, queries WebEngage for the user's complete history including tenure, total spend, order count, and loyalty tier, branches on VIP status for premium anniversary treatment, sends a heartfelt anniversary celebration email with a milestone recap and exclusive reward, waits 7 days for reward redemption, checks if the anniversary reward was claimed, sends a next-level teaser encouraging them toward the next loyalty milestone, and logs the anniversary event and outcomes to Google Sheets.",
    nodes: [
      trigger(
        "Schedule: Daily customer anniversary identification",
        "schedule",
        {
          schedule: "Every day at 9:00 AM",
          howItStarts:
            "Runs daily to find users whose sign-up anniversary falls on today, triggering a celebration journey for each qualifying customer",
          setup:
            "Configure daily schedule. Set WebEngage REST API key for anniversary segment queries. Configure email sending credentials. Set Google Sheets API credentials for milestone tracking.",
          inputFields: [],
        },
      ),

      // 2. WebEngage lookup user anniversary and loyalty data
      n(
        "2",
        "hitApi",
        "WebEngage: Fetch anniversary user profile with tenure and spend",
        280,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.webengage.com/v2/accounts/{webengage_account_id}/users/search",
            headers: [
              { key: "Authorization", value: "Bearer {webengage_api_key}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              { key: "segment", value: "anniversary_today" },
              {
                key: "attributes",
                value:
                  '["email","firstName","signupDate","loyaltyTier","pointsBalance","lifetimeValue","totalOrders","favoriteCategory","referralCount","nextTierThreshold"]',
              },
            ],
            variables: [
              {
                name: "users[0].email",
                customName: "user_email",
                type: "string",
              },
              {
                name: "users[0].firstName",
                customName: "user_name",
                type: "string",
              },
              {
                name: "users[0].signupDate",
                customName: "signup_date",
                type: "string",
              },
              {
                name: "users[0].loyaltyTier",
                customName: "loyalty_tier",
                type: "string",
              },
              {
                name: "users[0].pointsBalance",
                customName: "points_balance",
                type: "number",
              },
              {
                name: "users[0].lifetimeValue",
                customName: "lifetime_value",
                type: "number",
              },
              {
                name: "users[0].totalOrders",
                customName: "total_orders",
                type: "number",
              },
              {
                name: "users[0].favoriteCategory",
                customName: "favorite_category",
                type: "string",
              },
              {
                name: "users[0].referralCount",
                customName: "referral_count",
                type: "number",
              },
              {
                name: "users[0].nextTierThreshold",
                customName: "next_tier_threshold",
                type: "string",
              },
            ],
          },
        },
      ),

      // 3. Condition: VIP tier for premium treatment?
      n(
        "3",
        "condition",
        "VIP tier for premium anniversary celebration?",
        520,
        {
          conditionDetails: {
            conditions: [
              { variable: "loyalty_tier", operator: "equals", value: "Gold" },
            ],
            logicalOperator: "AND",
          },
        },
      ),

      // 4. Email anniversary celebration with milestone recap and reward
      emailNode(
        "4",
        "Email: Anniversary celebration with milestone recap and reward",
        760,
        "{user_email}",
        "Happy Anniversary, {user_name}! Celebrating Our Journey Together",
        "Hi {user_name},\n\nToday marks a special milestone — your anniversary with us! Since joining on {signup_date}, you have been an incredible part of our community.\n\nYour journey in numbers:\n- Total orders: {total_orders}\n- Lifetime value: {lifetime_value}\n- Current loyalty tier: {loyalty_tier}\n- Points earned: {points_balance}\n- Friends referred: {referral_count}\n- Favorite category: {favorite_category}\n\nTo celebrate this milestone, here is an exclusive anniversary reward:\nUse code ANNIV25 for 25% off + free shipping — valid for 14 days!\n\nPlus, we are adding 500 bonus loyalty points to your account as a thank-you.\n\nClaim your anniversary reward: https://app.example.com/anniversary-reward\n\nHere is to many more years together!\n\nWith gratitude,\nThe Team",
      ),

      // 5. Delay 7 days
      n(
        "5",
        "delayAction",
        "Wait 7 days for anniversary reward redemption",
        1000,
        {
          delayDetails: { duration: 7, unit: "days" },
        },
      ),

      // 6. Condition: anniversary reward redeemed?
      n("6", "condition", "Anniversary reward claimed?", 1240, {
        conditionDetails: {
          conditions: [
            { variable: "total_orders", operator: "greater_than", value: "0" },
          ],
          logicalOperator: "AND",
        },
      }),

      // 7. Email next-level teaser for next loyalty milestone
      emailNode(
        "7",
        "Email: Next loyalty milestone teaser and goal",
        1480,
        "{user_email}",
        "{user_name}, Your Next Milestone is Closer Than You Think!",
        "Hi {user_name},\n\nWe hope you are enjoying your anniversary reward! As a {loyalty_tier} member, here is something exciting to look forward to:\n\nYour next tier upgrade is just {next_tier_threshold} away! Here is what you will unlock:\n- Even higher points earning rates\n- Exclusive early access to sales and new arrivals\n- VIP-only events and experiences\n- Enhanced shipping and return benefits\n\nWith your love of {favorite_category}, we have picked some items that will help you reach your next milestone:\nhttps://app.example.com/for-you/{favorite_category}\n\nRemember, your ANNIV25 code is still active if you have not used it yet!\n\nKeep going strong!\n\nBest regards,\nLoyalty Team",
      ),

      // 8. Google Sheets log anniversary milestone and redemption
      sheetsNode(
        "8",
        "Google Sheets: Log anniversary milestone and outcomes",
        1720,
        "AnniversaryMilestoneLog",
        '[["{user_email}","{user_name}","{signup_date}","{loyalty_tier}","{points_balance}","{lifetime_value}","{total_orders}","{referral_count}","{next_tier_threshold}","logged"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_2"),
      e("7", "8"),
      e("3", "4", "output_2"),
    ],
  },

  // ===================================================================
  // 24. GEO-TARGETED STORE VISIT NUDGE — Pattern A (8 nodes)
  // ===================================================================

  "Geo-Targeted Store Visit Nudge": {
    summary:
      "Fires when WebEngage detects a user entering a geofence near a physical store via webhook, AI extracts user location, nearby store details, and device information from the payload, queries WebEngage for the user's profile including purchase history and in-store vs online preferences, branches on whether the user is an existing in-store customer or online-only, sends a personalized push-style email with store-specific offers and directions, waits 2 days, checks if the user visited the store or made a purchase, and sends a follow-up with an in-store exclusive offer or online alternative.",
    nodes: [
      trigger("WebEngage: User near physical store geofence", "webhook_app", {
        source: "webengage",
        event: "geofence.entered",
        howItStarts:
          "Fires when WebEngage detects a user's device entering a geofence radius around a physical store location, sending the raw event payload with location and user details",
        setup:
          "Configure WebEngage webhook for geofence entry events. Set WebEngage REST API key for user profile lookups. Set OpenAI API key for payload extraction. Configure email sending credentials.",
        inputFields: [],
      }),

      // 2. AI extract location and store details
      aiNode(
        "2",
        "AI: Extract user location and nearby store details",
        280,
        "You are a location data extraction agent. From the incoming WebEngage geofence event payload, extract the user email, full name, user latitude, user longitude, nearest store name, store address, store distance in meters, store phone number, store hours, and any current in-store promotions. Return JSON: {user_email, user_name, user_latitude, user_longitude, store_name, store_address, store_distance_meters, store_phone, store_hours, current_promotion}",
        "WebEngage geofence entry event payload: {webhook_payload}. Extract all location and store proximity fields regardless of payload format or field naming conventions.",
        [
          { field: "user_email", as: "user_email", type: "string" },
          { field: "user_name", as: "user_name", type: "string" },
          { field: "user_latitude", as: "user_latitude", type: "string" },
          { field: "user_longitude", as: "user_longitude", type: "string" },
          { field: "store_name", as: "store_name", type: "string" },
          { field: "store_address", as: "store_address", type: "string" },
          {
            field: "store_distance_meters",
            as: "store_distance_meters",
            type: "number",
          },
          { field: "store_phone", as: "store_phone", type: "string" },
          { field: "store_hours", as: "store_hours", type: "string" },
          {
            field: "current_promotion",
            as: "current_promotion",
            type: "string",
          },
        ],
      ),

      // 3. WebEngage lookup user profile and in-store history
      n(
        "3",
        "hitApi",
        "WebEngage: Fetch user profile with purchase and store visit history",
        520,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.webengage.com/v2/accounts/{webengage_account_id}/users/{user_email}",
            headers: [
              { key: "Authorization", value: "Bearer {webengage_api_key}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [],
            variables: [
              {
                name: "users.customAttributes.in_store_purchases",
                customName: "in_store_purchases",
                type: "number",
              },
              {
                name: "users.customAttributes.online_purchases",
                customName: "online_purchases",
                type: "number",
              },
              {
                name: "users.customAttributes.last_store_visit",
                customName: "last_store_visit",
                type: "string",
              },
              {
                name: "users.customAttributes.favorite_category",
                customName: "favorite_category",
                type: "string",
              },
              {
                name: "users.customAttributes.loyalty_tier",
                customName: "loyalty_tier",
                type: "string",
              },
            ],
          },
        },
      ),

      // 4. Condition: existing in-store customer?
      n("4", "condition", "User has previous in-store purchase history?", 760, {
        conditionDetails: {
          conditions: [
            {
              variable: "in_store_purchases",
              operator: "greater_than",
              value: "0",
            },
          ],
          logicalOperator: "AND",
        },
      }),

      // 5. Email store visit nudge with offers and directions
      emailNode(
        "5",
        "Email: Store visit nudge with location-specific offer",
        1000,
        "{user_email}",
        "{user_name}, You are Near {store_name} — Stop By for Something Special!",
        "Hi {user_name},\n\nWe noticed you are close to our {store_name} location and wanted to let you know about something special happening today!\n\nStore Details:\n- Address: {store_address}\n- Hours: {store_hours}\n- Phone: {store_phone}\n- Just {store_distance_meters}m away!\n\nCurrent In-Store Promotion:\n{current_promotion}\n\nAs a {loyalty_tier} member, show this email at checkout for an extra 10% off your in-store purchase today!\n\nWe have great new arrivals in {favorite_category} waiting for you to see in person.\n\nGet directions: https://maps.google.com/?q={store_address}\n\nSee you soon!\n\nBest regards,\nThe Store Team",
      ),

      // 6. Delay 2 days
      n("6", "delayAction", "Wait 2 days for store visit or purchase", 1240, {
        delayDetails: { duration: 2, unit: "days" },
      }),

      // 7. Condition: user visited store or made purchase?
      n(
        "7",
        "condition",
        "User visited store or purchased since nudge?",
        1480,
        {
          conditionDetails: {
            conditions: [
              {
                variable: "in_store_purchases",
                operator: "greater_than",
                value: "0",
              },
            ],
            logicalOperator: "AND",
          },
        },
      ),

      // 8. Email follow-up with in-store exclusive or online alternative
      emailNode(
        "8",
        "Email: Follow-up with in-store exclusive or online option",
        1720,
        "{user_email}",
        "Could Not Make It to {store_name}? Shop the Same Deals Online!",
        "Hi {user_name},\n\nWe understand life gets busy! If you were not able to visit {store_name} this time, no worries — you can enjoy the same great offers online.\n\nCurrent promotion available online:\n{current_promotion}\n\nPlus, browse our latest {favorite_category} collection from the comfort of home:\nhttps://app.example.com/category/{favorite_category}\n\nPrefer the in-store experience? {store_name} is open {store_hours} and our team would love to help you find exactly what you are looking for.\n\nBook a personal shopping appointment: https://app.example.com/book-appointment/{store_name}\n\nHowever you choose to shop, we are here for you!\n\nBest regards,\nThe Team",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_2"),
      e("4", "5", "output_2"),
    ],
  },

  // ===================================================================
  // 25. NEW ARRIVALS BY CATEGORY — Pattern D (8 nodes)
  // ===================================================================

  "New Arrivals by Category": {
    summary:
      "Fires when WebEngage detects new products added to a category that matches a user's preferences via webhook, AI extracts the product details, category information, and user targeting data from the payload, queries WebEngage for the user's profile to verify category interest and consent, branches on whether the new arrivals include items from the user's top preferred category, sends an immediate new arrivals alert email with curated product highlights and direct shopping links, waits 48 hours, checks if the user has browsed or purchased from the new arrivals, and sends a follow-up with trending picks or complementary category suggestions.",
    nodes: [
      trigger("WebEngage: New arrivals in preferred category", "webhook_app", {
        source: "webengage",
        event: "catalog.new_arrivals",
        howItStarts:
          "Fires when WebEngage detects new products have been added to a category that matches a user's preferences, sending the raw event payload with product and user targeting data",
        setup:
          "Configure WebEngage webhook for catalog new arrival events. Set WebEngage REST API key for user profile lookups. Set OpenAI API key for payload extraction. Configure email sending credentials.",
        inputFields: [],
      }),

      // 2. AI extract new arrival product details
      aiNode(
        "2",
        "AI: Extract new arrival products and category details",
        280,
        "You are a product catalog extraction agent. From the incoming WebEngage new arrivals event payload, extract the user email, full name, category name, number of new products added, top 3 product names as a comma-separated list, price range (min and max), featured product name, featured product price, featured product image URL, featured product page URL, and the collection landing page URL. Return JSON: {user_email, user_name, category_name, new_products_count, top_product_names, price_range_min, price_range_max, featured_product_name, featured_product_price, featured_product_image, featured_product_url, collection_url}",
        "WebEngage new arrivals catalog event payload: {webhook_payload}. Extract all product catalog and category fields regardless of payload format or field naming conventions.",
        [
          { field: "user_email", as: "user_email", type: "string" },
          { field: "user_name", as: "user_name", type: "string" },
          { field: "category_name", as: "category_name", type: "string" },
          {
            field: "new_products_count",
            as: "new_products_count",
            type: "number",
          },
          {
            field: "top_product_names",
            as: "top_product_names",
            type: "string",
          },
          { field: "price_range_min", as: "price_range_min", type: "string" },
          { field: "price_range_max", as: "price_range_max", type: "string" },
          {
            field: "featured_product_name",
            as: "featured_product_name",
            type: "string",
          },
          {
            field: "featured_product_price",
            as: "featured_product_price",
            type: "string",
          },
          {
            field: "featured_product_image",
            as: "featured_product_image",
            type: "string",
          },
          {
            field: "featured_product_url",
            as: "featured_product_url",
            type: "string",
          },
          { field: "collection_url", as: "collection_url", type: "string" },
        ],
      ),

      // 3. WebEngage verify user category interest and consent
      n(
        "3",
        "hitApi",
        "WebEngage: Verify user category interest and preferences",
        520,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.webengage.com/v2/accounts/{webengage_account_id}/users/{user_email}",
            headers: [
              { key: "Authorization", value: "Bearer {webengage_api_key}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [],
            variables: [
              {
                name: "users.customAttributes.preferred_categories",
                customName: "preferred_categories",
                type: "string",
              },
              {
                name: "users.customAttributes.email_opt_in",
                customName: "email_opt_in",
                type: "boolean",
              },
              {
                name: "users.customAttributes.last_purchase_category",
                customName: "last_purchase_category",
                type: "string",
              },
              {
                name: "users.customAttributes.average_order_value",
                customName: "avg_order_value",
                type: "string",
              },
              {
                name: "users.customAttributes.total_orders",
                customName: "total_orders",
                type: "number",
              },
            ],
          },
        },
      ),

      // 4. Condition: new arrivals in user's top preferred category?
      n(
        "4",
        "condition",
        "New arrivals match user's top preferred category?",
        760,
        {
          conditionDetails: {
            conditions: [
              {
                variable: "new_products_count",
                operator: "greater_than",
                value: "3",
              },
            ],
            logicalOperator: "AND",
          },
        },
      ),

      // 5. Email new arrivals alert with curated product highlights
      emailNode(
        "5",
        "Email: New arrivals alert with curated product highlights",
        1000,
        "{user_email}",
        "Just Dropped: {new_products_count} New Arrivals in {category_name}!",
        "Hi {user_name},\n\nExciting news! We just added {new_products_count} fresh new items to {category_name} — and since it is one of your favorite categories, we wanted you to be the first to know.\n\nFeatured Pick:\n{featured_product_name} — {featured_product_price}\nShop now: {featured_product_url}\n\nMore new arrivals you will love:\n{top_product_names}\n\nPrices range from {price_range_min} to {price_range_max} — something for every budget.\n\nBrowse the full new {category_name} collection: {collection_url}\n\nNew items sell fast, so grab your favorites before they are gone!\n\nHappy shopping!\n\nBest regards,\nThe Team",
      ),

      // 6. Delay 48 hours
      n(
        "6",
        "delayAction",
        "Wait 48 hours for browse or purchase activity",
        1240,
        {
          delayDetails: { duration: 48, unit: "hours" },
        },
      ),

      // 7. Condition: user browsed or purchased from new arrivals?
      n("7", "condition", "User engaged with new arrivals?", 1480, {
        conditionDetails: {
          conditions: [
            { variable: "total_orders", operator: "greater_than", value: "0" },
          ],
          logicalOperator: "AND",
        },
      }),

      // 8. Email trending picks or complementary category suggestions
      emailNode(
        "8",
        "Email: Trending picks and complementary category suggestions",
        1720,
        "{user_email}",
        "Trending Now in {category_name} + Categories You Will Love",
        "Hi {user_name},\n\nThe new {category_name} arrivals have been flying off the shelves! Here is what is trending:\n\nMost Popular New Arrivals:\n{top_product_names}\n\nStill available: {featured_product_name} at {featured_product_price}\nShop: {featured_product_url}\n\nSince you love {category_name}, you might also enjoy exploring:\n- {last_purchase_category}: Fresh picks that complement your recent purchases\n- Staff favorites across all categories\n- Items trending with customers who shop {category_name}\n\nBrowse all new arrivals: {collection_url}\n\nWith {total_orders} orders under your belt, you know great style when you see it!\n\nBest regards,\nThe Team",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_2"),
      e("4", "5", "output_2"),
    ],
  },
};
